import React, {
  createContext, useContext, useEffect, useMemo, useState, useCallback,
} from "react";

/**
 * LocationContext — 6-state freshness model (Slice 1).
 *
 * The old context had 3 sources (default / gps / manual) and *did not*
 * differentiate a 5-second-old GPS fix from a 20-minute-old cached one.
 * That's what caused "Live" labels on stale readings.
 *
 * We now store a single `source` string ("gps" | "manual" | "default") and
 * a `fixed_at` ISO timestamp. Consumers read `state` (computed) instead:
 *
 *   live       — GPS fix < 60 s old              (green, pulsing)
 *   recent     — GPS fix 60 s – 15 min old       (green)
 *   manual     — user chose a city / dropped pin (blue)
 *   last_known — GPS fix 15 min – 24 h old       (amber)
 *   cached     — GPS fix > 24 h old              (orange)
 *   default    — never got a real fix            (grey — the Mumbai fallback)
 *
 * Backward compat: `loc.source` still exists ("gps"/"manual"/"default"),
 * `loc.city/lat/lng` unchanged. New: `loc.fixed_at`, `state`, `age_ms`,
 * `age_label`, plus a `refresh()` alias for `detect()`.
 */
const LocationContext = createContext(null);
export const useLocation = () => useContext(LocationContext);

const DEFAULT = {
  lat: 19.076, lng: 72.8777, city: "Mumbai",
  source: "default", fixed_at: null,
};
const CACHE_KEY = "fn_location_v2";
const CACHE_MS  = 30 * 24 * 60 * 60 * 1000;   // 30 days (state is computed at read)

// State thresholds
const LIVE_MS       = 60 * 1000;               //  <  1 min
const RECENT_MS     = 15 * 60 * 1000;          //  <  15 min
const LAST_KNOWN_MS = 24 * 60 * 60 * 1000;     //  <  24 h

async function reverseGeocode(lat, lng) {
  try {
    const r = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=12`,
      { headers: { "Accept-Language": "en" } }
    );
    if (!r.ok) return null;
    const j = await r.json();
    const a = j.address || {};
    return (
      a.suburb || a.neighbourhood || a.city_district || a.city || a.town ||
      a.village || a.county || a.state_district || a.state || null
    );
  } catch { return null; }
}

/** Compute freshness state from a location object. Pure — no side-effects. */
export function computeLocationState(loc, now = Date.now()) {
  if (!loc || loc.source === "default") return "default";
  if (loc.source === "manual") return "manual";
  if (loc.source !== "gps" || !loc.fixed_at) return "default";
  const t = Date.parse(loc.fixed_at);
  if (!Number.isFinite(t)) return "default";
  const age = now - t;
  if (age < 0)             return "live";        // clock skew — treat as live
  if (age < LIVE_MS)       return "live";
  if (age < RECENT_MS)     return "recent";
  if (age < LAST_KNOWN_MS) return "last_known";
  return "cached";
}

/** Human-readable age label ("just now", "5m ago", …). Pure. */
export function ageLabel(loc, now = Date.now()) {
  if (!loc?.fixed_at) return loc?.source === "manual" ? "picked" : "unknown";
  const t = Date.parse(loc.fixed_at);
  if (!Number.isFinite(t)) return "unknown";
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 10)      return "just now";
  if (s < 60)      return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60)      return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24)      return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30)      return `${d}d ago`;
  return "long ago";
}

export function LocationProvider({ children }) {
  const [loc, setLoc] = useState(() => {
    try {
      const c = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
      // Newer format: {data,ts}. If it's a stale v1 entry the shape still works
      // because `fixed_at` will be missing → state falls through to default.
      if (c && Date.now() - c.ts < CACHE_MS) return c.data;
    } catch {}
    // One-time migration from v1
    try {
      const c1 = JSON.parse(localStorage.getItem("fn_location_v1") || "null");
      if (c1?.data) {
        const migrated = {
          ...c1.data,
          fixed_at: c1.data.source === "gps"
            ? new Date(c1.ts || Date.now()).toISOString()
            : null,
        };
        return migrated;
      }
    } catch {}
    return DEFAULT;
  });
  const [permission, setPermission] = useState("unknown");   // unknown|granted|denied|prompt
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  // Tick every 30 s so age labels & state ("live"→"recent") re-render without
  // a full app reload. Cheap: only re-renders subscribers via useMemo below.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const save = (data) => {
    setLoc(data);
    try { localStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() })); } catch {}
  };

  const detect = useCallback(async () => {
    if (!("geolocation" in navigator)) { setError("Geolocation not supported"); return null; }
    setLoading(true); setError(null);
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude, longitude } = pos.coords;
          const city = (await reverseGeocode(latitude, longitude)) || loc.city || "Nearby";
          const data = {
            lat: latitude, lng: longitude, city,
            source: "gps",
            fixed_at: new Date(pos.timestamp || Date.now()).toISOString(),
          };
          save(data);
          setPermission("granted");
          setLoading(false);
          resolve(data);
        },
        (err) => {
          setError(err.message);
          setPermission(err.code === err.PERMISSION_DENIED ? "denied" : "prompt");
          setLoading(false);
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    });
  }, [loc.city]);

  useEffect(() => {
    if (!("permissions" in navigator)) return;
    navigator.permissions?.query?.({ name: "geolocation" }).then((s) => {
      setPermission(s.state);
      if (s.state === "granted") detect();
      s.onchange = () => setPermission(s.state);
    }).catch(() => {});
  }, [detect]);

  // A city without known coordinates must not inherit the previous city's lat/lng (that showed Mumbai as "Hapur").
  const setManualCity = (city) => save({ lat: null, lng: null, city, source: "manual",
                                          fixed_at: new Date().toISOString() });

  const setCity = useCallback(async (city, lat, lng) => {
    if (!city) return loc;
    const nowIso = new Date().toISOString();
    if (lat != null && lng != null) {
      const data = { lat: Number(lat), lng: Number(lng), city,
                     source: "manual", fixed_at: nowIso };
      save(data);
      return data;
    }
    try {
      const r = await fetch(
        `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&q=${encodeURIComponent(city)}`,
        { headers: { "Accept-Language": "en" } }
      );
      if (r.ok) {
        const arr = await r.json();
        if (arr && arr[0]) {
          const data = { lat: parseFloat(arr[0].lat), lng: parseFloat(arr[0].lon),
                         city, source: "manual", fixed_at: nowIso };
          save(data);
          return data;
        }
      }
    } catch {}
    const data = { lat: null, lng: null, city, source: "manual", fixed_at: nowIso };
    save(data);
    return data;
  }, [loc]);

  // Derived: state + age_label (memoised on loc + ticking clock).
  const derived = useMemo(() => ({
    state:     computeLocationState(loc, now),
    age_ms:    loc?.fixed_at ? Math.max(0, now - Date.parse(loc.fixed_at)) : null,
    age_label: ageLabel(loc, now),
  }), [loc, now]);

  const value = {
    loc, permission, loading, error, detect,
    refresh: detect,           // alias
    setManualCity, setCity,
    ...derived,                // state, age_ms, age_label
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
}
